/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/qaExecutionCache.ts
 * FILE_ID: JOBHUB-QA-EXEC-CACHE-001
 * PURPOSE: Patch one QA area cache and repack display numbers.
 */
import type { QaArea, QaAreaExecution, QaChecklistInstance, QaEvidence, QaItemInstance } from '../../qa/qaTypes'
import { withQaDisplayNumbers } from '../../qa/qaDisplayNumbers'

const generations = new Map<string, number>()

export function currentWriteGeneration(areaId: string): number {
  return generations.get(areaId) ?? 0
}

export function bumpWriteGeneration(areaId: string): number {
  const next = currentWriteGeneration(areaId) + 1
  generations.set(areaId, next)
  return next
}

const held = new Set<string>()

export function holdQaArea(areaId: string): void {
  held.add(areaId)
  bumpWriteGeneration(areaId)
}

export function releaseQaArea(areaId: string): void {
  held.delete(areaId)
  bumpWriteGeneration(areaId)
}

export function keepCachedArea(
  areaId: string,
  startedGeneration: number,
  fetched: QaAreaExecution,
  cached: QaAreaExecution | undefined,
): QaAreaExecution {
  if (!cached) return fetched
  if (held.has(areaId) || currentWriteGeneration(areaId) !== startedGeneration) return cached
  return fetched
}

export function withAssignee<T extends object>(
  row: T,
  person: { userId: number; name: string; dueDate: string; email?: string | null },
): T {
  return {
    ...row,
    assigned_to: person.userId,
    assigned_to_name: person.name,
    ...(person.email !== undefined ? { assigned_to_email: person.email } : {}),
    ...(person.dueDate ? { due_date: person.dueDate } : {}),
  } as T
}

function numbered(area: QaAreaExecution): QaAreaExecution {
  return withQaDisplayNumbers(area)
}

export function patchItem(area: QaAreaExecution, item: QaItemInstance): QaAreaExecution {
  return numbered({
    ...area,
    checklists: area.checklists.map((checklist) => ({
      ...checklist,
      items: checklist.items.map((row) => (row.id === item.id ? { ...row, ...item } : row)),
    })),
  })
}

export function reorderAreaItems(area: QaAreaExecution, orderedIds: string[]): QaAreaExecution {
  const rank = new Map(orderedIds.map((id, index) => [id, index]))
  return numbered({
    ...area,
    checklists: area.checklists.map((checklist) => ({
      ...checklist,
      items: [...checklist.items]
        .sort((left, right) => (rank.get(left.id) ?? Number.MAX_SAFE_INTEGER) - (rank.get(right.id) ?? Number.MAX_SAFE_INTEGER))
        .map((item, index) => ({ ...item, sort_order: index })),
    })),
  })
}

export function appendItem(area: QaAreaExecution, checklistId: string, item: QaItemInstance): QaAreaExecution {
  return numbered({
    ...area,
    checklists: area.checklists.map((checklist) => (
      checklist.id === checklistId ? { ...checklist, items: [...checklist.items, item] } : checklist
    )),
  })
}

function descendantIds(items: QaItemInstance[], roots: string[]): Set<string> {
  const drop = new Set(roots)
  let grew = true
  while (grew) {
    grew = false
    for (const item of items) {
      if (item.parent_item && drop.has(item.parent_item) && !drop.has(item.id)) {
        drop.add(item.id)
        grew = true
      }
    }
  }
  return drop
}

export function removeItems(area: QaAreaExecution, ids: string[]): QaAreaExecution {
  return numbered({
    ...area,
    checklists: area.checklists.map((checklist) => {
      const drop = descendantIds(checklist.items, ids)
      return { ...checklist, items: checklist.items.filter((item) => !drop.has(item.id)) }
    }),
  })
}

export function patchChecklist(area: QaAreaExecution, checklist: QaChecklistInstance): QaAreaExecution {
  return numbered({
    ...area,
    checklists: area.checklists.map((row) => (row.id === checklist.id ? { ...row, ...checklist, items: checklist.items ?? row.items } : row)),
  })
}

export function appendChecklist(area: QaAreaExecution, checklist: QaChecklistInstance): QaAreaExecution {
  return numbered({ ...area, checklists: [...area.checklists, checklist] })
}

export function removeChecklist(area: QaAreaExecution, id: string): QaAreaExecution {
  return numbered({ ...area, checklists: area.checklists.filter((row) => row.id !== id) })
}

export function addEvidence(area: QaAreaExecution, evidence: QaEvidence): QaAreaExecution {
  return { ...area, evidence: [...(area.evidence ?? []), evidence] }
}

export function removeEvidence(area: QaAreaExecution, id: string): QaAreaExecution {
  return { ...area, evidence: (area.evidence ?? []).filter((row) => row.id !== id) }
}

export function renameAreaIn<T extends { id: string; area_name: string }>(list: T[], area: { id: string; area_name: string }): T[] {
  return list.map((row) => (row.id === area.id ? { ...row, ...area } : row))
}

export function removeAreaFrom<T extends { id: string }>(list: T[], id: string): T[] {
  return list.filter((row) => row.id !== id)
}

export type QaAreaRow = QaArea
